// lib/microsite/catalogGate.ts
//
// Gate de catálogo de la tienda online. El POS separa la grilla de comida del
// bar (items.is_bar) y de la tienda física (items.is_retail) — ver
// app/[slug]/(pos)/pos/page.tsx — pero el microsite público cargaba TODO sin
// filtro: tragos y productos físicos salían online sin que el dueño lo
// decidiera. Ambos flags son opt-in (default ocultos) desde Ajustes →
// Funcionalidades.
import type { LocationFeatures } from "@/types/location-features"

export type OnlineCatalogExclusion = "is_bar" | "is_retail"

/**
 * Columnas de `items` que la tienda online debe excluir (`.eq(col, false)`)
 * según los features del local. Ambas son NOT NULL DEFAULT false en la DB
 * (migraciones 20260612025746 y 20260622184733), así que `.eq(false)` no
 * descarta filas legacy con NULL.
 */
export function onlineCatalogExclusions(
  features: Pick<LocationFeatures, "online_show_bar" | "online_show_retail">,
): OnlineCatalogExclusion[] {
  const cols: OnlineCatalogExclusion[] = []
  if (!features.online_show_bar) cols.push("is_bar")
  if (!features.online_show_retail) cols.push("is_retail")
  return cols
}

/**
 * Sin ítems visibles no hay pestaña: una categoría cuyo contenido quedó gateado
 * (p.ej. "Bar" con online_show_bar=false) no debe rendear un tab vacío en el
 * microsite. También cubre categorías vacías por 86/inactivos.
 */
export function dropEmptyCategories<C extends { id: string }>(
  categories: C[],
  items: Array<{ category_id: string | null }>,
): C[] {
  const withItems = new Set(items.map((i) => i.category_id))
  return categories.filter((c) => withItems.has(c.id))
}

/** Datos mínimos de un ítem para decidir si la tienda online sabe cobrarlo. */
export type OnlineRetailFacts = {
  pricing_mode?: string | null
  has_variants?: boolean | null
  is_retail?: boolean | null
}

/**
 * PR 1/2 del súper online: la bandera `online_retail_v2` más `online_show_retail`.
 * Solo con las dos prendidas la tienda ofrece peso/variantes (lo que PR 2 habilita).
 */
export function onlineRetailV2(
  features: Pick<LocationFeatures, "online_show_retail" | "online_retail_v2">,
): boolean {
  return features.online_show_retail === true && features.online_retail_v2 === true
}

/**
 * Razón por la que la tienda online todavía no puede cobrar este ítem, o null.
 * Peso, variantes y retail con modificadores no tienen cotización online; un
 * plato de restaurante con modificadores (is_retail falso) no entra aquí.
 */
export function onlineRetailUnsupportedReason(
  item: OnlineRetailFacts,
  lineHasModifiers: boolean,
): "retail_not_supported_online" | null {
  if (item.pricing_mode === "weight") return "retail_not_supported_online"
  if (item.has_variants === true) return "retail_not_supported_online"
  if (item.is_retail === true && lineHasModifiers) return "retail_not_supported_online"
  return null
}

/**
 * Oculta del catálogo online lo retail que no se sabe cobrar. Con
 * `online_show_retail` apagado (todo restaurante) o con `online_retail_v2`
 * prendida devuelve el MISMO array: cero cambio para quien no usa retail.
 */
export function hideUnsupportedOnlineRetail<
  T extends OnlineRetailFacts & { item_modifier_groups?: unknown[] | null },
>(
  items: T[],
  features: Pick<LocationFeatures, "online_show_retail" | "online_retail_v2">,
): T[] {
  if (!features.online_show_retail || onlineRetailV2(features)) return items
  return items.filter(
    (i) => onlineRetailUnsupportedReason(i, (i.item_modifier_groups?.length ?? 0) > 0) === null,
  )
}

// ── Lo que la tienda online NO vende (súper online PR 1, Tarea 1.4) ──────────
//
// Decisión de Samuel (2026-10-21): la tienda online y el kiosco no venden alcohol
// ni tabaco; solo la caja, con verificación de edad (spec de la caja de súper,
// PR #2608 §5.0). Y lo retail con `online_show_retail` apagado tampoco: el dueño lo
// escondió de la tienda, y el servidor lo vendía igual si llegaba en el carrito.

/**
 * Columnas de `items` que marcan lo restringido. Se suman a los SELECT de la tienda
 * (micrositio, sugerencias online, cotizador, plan B de sustituciones y stock retail)
 * tal cual: columnas de más en consultas que ya existen, cero round-trips nuevos.
 *
 * `is_alcohol` viene de la migración 20261021230147 (ya en prod). `is_tobacco` la crea
 * 20261022173522_retail_age_verification.sql (caja de súper, PR 4 — #2610): este PR NO
 * puede mergear antes de que esa migración esté aplicada en prod, porque pedir una
 * columna que no existe tumba la consulta (y con ella la tienda de TODO local).
 *
 * No se usa `selectWithOptionalColumns` (lib/supabase/optionalColumns.ts) a
 * propósito: mientras la columna no existiera, cada carga de restaurante haría una
 * consulta fallida + reintento = un round-trip extra en el camino de restaurante.
 */
export const ONLINE_RESTRICTED_COLUMNS = ["is_alcohol", "is_tobacco"] as const

/** Datos mínimos de un ítem para decidir si online lo vende. Una clave ausente
 *  cuenta como `false`. */
export type OnlineRestrictedFacts = {
  is_retail?: boolean | null
  is_alcohol?: boolean | null
  is_tobacco?: boolean | null
}

/**
 * Alcohol o tabaco RETAIL. Exige `is_retail`: un restaurante puede marcar su cerveza
 * `is_alcohol` para las promociones (los cupones excluyen alcohol) y esa cerveza se
 * sigue vendiendo online como en main. Sin señal retail no cambia nada.
 */
export function isOnlineRestricted(row: OnlineRestrictedFacts): boolean {
  return row.is_retail === true && (row.is_alcohol === true || row.is_tobacco === true)
}

/**
 * Con `features.age_verification` (caja de súper, PR 4 — Task 18) la tienda online
 * tampoco vende el alcohol ni el tabaco NO retail: el local verifica la edad en la
 * caja, y online no hay quién la verifique. Sin la bandera es exactamente
 * `isOnlineRestricted` (main): la cerveza de un restaurante, marcada `is_alcohol`
 * para las promociones, se sigue vendiendo online.
 */
function isRestrictedOnlineFor(
  row: OnlineRestrictedFacts,
  features: Pick<LocationFeatures, "age_verification">,
): boolean {
  if (isOnlineRestricted(row)) return true
  return features.age_verification === true && (row.is_alcohol === true || row.is_tobacco === true)
}

/**
 * Razón por la que online no vende este ítem, o null.
 *   - "restricted": alcohol o tabaco retail (nunca online); con `age_verification`,
 *     también el alcohol o el tabaco NO retail (Task 18), con el mismo código.
 *   - "retail_hidden": retail con `online_show_retail` apagado.
 * Sin `age_verification`, un plato de restaurante (`is_retail` falso) da null siempre,
 * aunque tenga `is_alcohol`: idéntico a main.
 */
export function onlineNotSoldReason(
  row: OnlineRestrictedFacts,
  features: Pick<LocationFeatures, "online_show_retail" | "age_verification">,
): "restricted" | "retail_hidden" | null {
  if (isRestrictedOnlineFor(row, features)) return "restricted"
  if (row.is_retail === true && !features.online_show_retail) return "retail_hidden"
  return null
}

/**
 * Saca del catálogo online el alcohol y el tabaco retail; con `age_verification`
 * (Task 18), también el NO retail. Sin la bandera y con `online_show_retail` apagado
 * (todo restaurante) devuelve el MISMO array: la consulta ya excluyó todo `is_retail`
 * (`onlineCatalogExclusions`). Sin ítems restringidos, también la misma referencia.
 */
export function hideRestrictedOnline<T extends OnlineRestrictedFacts>(
  items: T[],
  features: Pick<LocationFeatures, "online_show_retail" | "age_verification">,
): T[] {
  if (features.age_verification === true) {
    // Con la bandera, lo restringido no retail sale aunque `online_show_retail` esté
    // apagado: la consulta no lo excluye (`onlineCatalogExclusions` no cambia).
    const restricted = (i: T) => isRestrictedOnlineFor(i, features)
    if (!items.some(restricted)) return items
    return items.filter((i) => !restricted(i))
  }
  if (!features.online_show_retail) return items
  if (!items.some(isOnlineRestricted)) return items
  return items.filter((i) => !isOnlineRestricted(i))
}

// ── Con `online_retail_v2` (súper online PR 2a, Tarea 2.6) ────────────────────

/**
 * Ítems que el JSON-LD (SEO) puede anunciar. Con `online_retail_v2` la tienda lista
 * el retail (peso, variantes), pero el JSON-LD no: su disponibilidad en vivo es del
 * PR 5. Sin la bandera devuelve el MISMO array (el catálogo ya viene filtrado por
 * `hideUnsupportedOnlineRetail`), así que ningún restaurante cambia.
 */
export function jsonLdItemsOnline<T extends { is_retail?: boolean | null }>(
  items: T[],
  features: Pick<LocationFeatures, "online_show_retail" | "online_retail_v2">,
): T[] {
  if (!onlineRetailV2(features)) return items
  return items.filter((i) => i.is_retail !== true)
}

/**
 * Ítems que una sugerencia online puede agregar al carrito. La sugerencia agrega
 * directo, sin abrir el selector de peso o de variante, así que con
 * `online_retail_v2` (que deja pasar peso y variantes al catálogo) esos dos quedan
 * fuera. Sin la bandera devuelve el MISMO array: `hideUnsupportedOnlineRetail` ya
 * los sacó o no hay retail.
 */
export function hideUnpickableOnlineSuggestions<T extends OnlineRetailFacts>(
  items: T[],
  features: Pick<LocationFeatures, "online_show_retail" | "online_retail_v2">,
): T[] {
  if (!onlineRetailV2(features)) return items
  return items.filter((i) => i.pricing_mode !== "weight" && i.has_variants !== true)
}
